import { Carousel, MCarousel, utils } from '@weapp/ui';
import { classnames } from '@weapp/utils';
import isEmpty from 'lodash-es/isEmpty';
import { toJS } from 'mobx';
import React, { PropsWithRef, PureComponent } from 'react';
import { When } from 'react-if';
import { LayoutType, pageViewClsPrefix } from '../../constants';
import ebdcoms from '../../utils/ebdcoms';
import './Styled.less';
import {
  BackgroundMaskProps, BackgroundType, FillMode, SizeType,
} from './types';
import {
  getCustomProps,
  getFormatVideoStyles,
  getIsEmptyPageBgConfig,
  getPictureStyles,
} from './utils';

export default class BackgroundMask extends PureComponent<PropsWithRef<BackgroundMaskProps>> {
  wrapRef = React.createRef<HTMLDivElement>();

  state = {
    carouselItems: [],
    videoStyles: {},
  };

  resetDftPicPath = () => {
    if (this.dftPicPath) {
      this.dftPicPath = ebdcoms?.excu('getImgPath', true)('background/emptyPicture.png');
    }
    return this.dftPicPath || '';
  };

  dftPicPath: String = this.resetDftPicPath();

  componentDidMount() {
    window.addEventListener('resize', this.getBackgroundStyles);

    this.resetDftPicPath();
    // componentDidMount中可直接取到ref示例，不需要加定时器
    this.getBackgroundStyles(this.wrapRef?.current || undefined);
  }

  getSnapshotBeforeUpdate(prePrps: BackgroundMaskProps) {
    this.resetDftPicPath();
    if (!utils.isEqual(prePrps.pageBgConfig, this.props.pageBgConfig)) {
      this.setState({
        carouselItems: this.renderCarouselItem(),
      });
      this.getBackgroundStyles();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.getBackgroundStyles);
  }

  getBackgroundStyles: any = (wrapDom?: HTMLDivElement) => {
    const { pageBgConfig } = this.props;
    const styles = getFormatVideoStyles(pageBgConfig, this.wrapRef?.current || undefined);

    this.setState({
      videoStyles: styles,
    });

    // 动态背景为多图时，且填充方式为填充或适应时，需要提前加载图片
    if (pageBgConfig?.type === BackgroundType.Picture) {
      if ([FillMode.Fill, FillMode.Fit].includes(pageBgConfig?.fillMode)) {
        const onloadImgs = [];
        const getImgUrl = ebdcoms.excu('getImgUrl');

        pageBgConfig?.pictureSetting?.forEach((item: any) => {
          if (item?.url) {
            const image = new Image();
            image.src = getImgUrl(item?.url);
            image.onload = () => {
              onloadImgs.push(item?.id);

              if (onloadImgs.length === pageBgConfig?.pictureSetting?.length) {
                this.setState({
                  carouselItems: this.renderCarouselItem(wrapDom),
                });
              }
            };
          }
        });
      } else {
        this.setState({
          carouselItems: this.renderCarouselItem(wrapDom),
        });
      }
    }
  };

  renderCarouselItem = (wrapDom?: HTMLDivElement) => {
    const { pageBgConfig } = this.props;

    // 网格布局pageBgConfig默认值为{ isEmpty: false }
    if (isEmpty(pageBgConfig) || isEmpty(pageBgConfig?.type)) return null;

    const { pictureSetting, fillMode } = pageBgConfig!;

    const getImgUrl = ebdcoms.excu('getImgUrl');

    // 流式布局返回的pictureSetting为mobx类型
    return JSON.parse(JSON.stringify(toJS(pictureSetting))).map((item: any) => {
      const { id, url } = item;

      const styles = getPictureStyles(
        {
          ...item,
          fillMode,
        },
        wrapDom || this.wrapRef?.current || undefined,
      );

      return (
        <div
          key={id}
          style={{
            width: '100%',
            height: '100%',
            backgroundImage: `url(${getImgUrl(url) || this.dftPicPath})`,
            ...styles,
          }}
        />
      );
    });
  };

  render() {
    const { pageBgConfig, clientType, layoutType = LayoutType.GridLayout } = this.props;
    const { videoStyles } = this.state;
    const isEmptyBgConfig = getIsEmptyPageBgConfig(pageBgConfig);

    // 网格布局pageBgConfig默认值为{ isEmpty: false }
    if (isEmptyBgConfig) {
      return null;
    }

    const CarouselCom = clientType === 'MOBILE' ? MCarousel : Carousel;

    const { type, sizeType } = pageBgConfig!;

    const customProps = getCustomProps(pageBgConfig);

    return (
      <div
        ref={this.wrapRef}
        className={classnames(`${pageViewClsPrefix}-background-mask`, {
          flowLayout: layoutType === LayoutType.FlowLayout,
        })}
      >
        <When weId={`${this.props.weId || ''}_zoq46d`} condition={type === BackgroundType.Picture}>
          <CarouselCom
            weId={`${this.props.weId || ''}_1wmrs3`}
            navigation={false}
            pagination={false}
            effect="fade"
            fadeEffect={{
              crossFade: true,
            }}
            {...customProps}
          >
            {this.state.carouselItems}
          </CarouselCom>
        </When>
        <When weId={`${this.props.weId || ''}_zoq46d`} condition={type === BackgroundType.Video}>
          <video
            weId={`${this.props.weId || ''}_2muhtm`}
            controls={false}
            autoPlay
            // `${pageViewClsPrefix}-background-video`：背景图视频标识，保存缩略图是需要根据此className保证不被筛选掉
            className={classnames(`${pageViewClsPrefix}-background-video`, {
              [`${pageViewClsPrefix}-background-video-full-screen`]:
                sizeType === SizeType.FullScreen,
            })}
            loop
            muted
            {...customProps}
            style={sizeType === SizeType.FixedSize ? videoStyles : {}}
          />
        </When>
      </div>
    );
  }
}
